import "./index.less";
import { AtParagraph } from "@atman/design-system";
import { AtSubScaleNavigation } from "../AtSubScaleNavigation";
import { AtSubScaleNavigationItem } from "../AtSubScaleNavigation/components/AtSubScaleNavigationItem";
import { IInterpretationData } from "@atman/business";
import { action, computed, isObservableArray, observable } from "mobx";
import { inject, observer } from "mobx-react";
import React from "react";

export enum InterpretationDataViewTabType {
    Interpretation,
    Strengths,
    Advices,
    Elements,
}

export type InterpretationDataViewTab = { type: InterpretationDataViewTabType; navigationItem: JSX.Element };

export interface IInterpretationDataViewProps {
    data: IInterpretationData;
    showAllTabs?: boolean;
}

@inject()
@observer
export class InterpretationDataView extends React.Component<IInterpretationDataViewProps, {}> {
    public static defaultProps = {
        showAllTabs: false,
    };

    @observable currentTab: InterpretationDataViewTabType;

    constructor(props) {
        super(props);

        const { data } = this.props;

        if (data.interpretation) {
            this.currentTab = InterpretationDataViewTabType.Interpretation;
        } else if (data.elements.any()) {
            this.currentTab = InterpretationDataViewTabType.Elements;
        } else if (data.pros.any()) {
            this.currentTab = InterpretationDataViewTabType.Strengths;
        } else if (data.advices.any()) {
            this.currentTab = InterpretationDataViewTabType.Advices;
        } else {
            this.currentTab = InterpretationDataViewTabType.Interpretation;
        }
    }

    @computed get numberOfOptions(): number {
        const {
            data: { interpretation, elements, advices, pros },
        } = this.props;

        const values = [!!interpretation, elements.any(), advices.any(), pros.any()];

        return values.filter((x) => x).length;
    }

    @action onFilterItemClick = (tab: InterpretationDataViewTabType) => {
        this.currentTab = tab;
    };

    renderContentForTab(tab: InterpretationDataViewTabType) {
        const { data } = this.props;

        let text: string | string[];

        switch (tab) {
            case InterpretationDataViewTabType.Interpretation:
                text = data.interpretation;
                break;
            case InterpretationDataViewTabType.Elements:
                text = data.elements;
                break;
            case InterpretationDataViewTabType.Strengths:
                text = data.pros;
                break;
            case InterpretationDataViewTabType.Advices:
                text = data.advices;
                break;
        }

        return this.renderText(text);
    }

    renderText(text: string | string[]) {
        if (!isObservableArray(text)) {
            return <AtParagraph>{text}</AtParagraph>;
        }

        return (
            <ul>
                {text.map((x) => (
                    <li key={x}>
                        <AtParagraph>{x}</AtParagraph>
                    </li>
                ))}
            </ul>
        );
    }

    render() {
        const {
            data: { pros, elements, advices, interpretation },
        } = this.props;

        const viewTabs: InterpretationDataViewTab[] = [];

        if (interpretation) {
            viewTabs.push({
                type: InterpretationDataViewTabType.Interpretation,
                navigationItem: (
                    <AtSubScaleNavigationItem
                        icon={["far", "info-circle"]}
                        selected={this.currentTab === InterpretationDataViewTabType.Interpretation}
                        onClick={() => this.onFilterItemClick(InterpretationDataViewTabType.Interpretation)}
                    >
                        {"psychometrics.interpretation".localize()}
                    </AtSubScaleNavigationItem>
                ),
            });
        }
        if (elements?.any()) {
            viewTabs.push({
                type: InterpretationDataViewTabType.Elements,
                navigationItem: (
                    <AtSubScaleNavigationItem
                        icon={["far", "info-circle"]}
                        selected={this.currentTab === InterpretationDataViewTabType.Elements}
                        onClick={() => this.onFilterItemClick(InterpretationDataViewTabType.Elements)}
                    >
                        {"psychometrics.interpretation".localize()}
                    </AtSubScaleNavigationItem>
                ),
            });
        }
        if (pros?.any()) {
            viewTabs.push({
                type: InterpretationDataViewTabType.Strengths,
                navigationItem: (
                    <AtSubScaleNavigationItem
                        icon={["far", "fist-raised"]}
                        selected={this.currentTab === InterpretationDataViewTabType.Strengths}
                        onClick={() => this.onFilterItemClick(InterpretationDataViewTabType.Strengths)}
                    >
                        {"psychometrics.strengths".localize()}
                    </AtSubScaleNavigationItem>
                ),
            });
        }
        if (advices?.any()) {
            viewTabs.push({
                type: InterpretationDataViewTabType.Advices,
                navigationItem: (
                    <AtSubScaleNavigationItem
                        icon={["far", "lightbulb"]}
                        selected={this.currentTab === InterpretationDataViewTabType.Advices}
                        onClick={() => this.onFilterItemClick(InterpretationDataViewTabType.Advices)}
                    >
                        {"psychometrics.advices".localize()}
                    </AtSubScaleNavigationItem>
                ),
            });
        }

        const { showAllTabs } = this.props;

        return (
            <>
                {!showAllTabs ? (
                    <AtSubScaleNavigation navigationItems={viewTabs.map((x) => x.navigationItem)}>
                        {this.renderContentForTab(this.currentTab)}
                    </AtSubScaleNavigation>
                ) : (
                    viewTabs.map((tab) => (
                        <AtSubScaleNavigation key={tab.navigationItem.key} navigationItems={tab.navigationItem}>
                            {this.renderContentForTab(tab.type)}
                        </AtSubScaleNavigation>
                    ))
                )}
            </>
        );
    }
}
